import { useEffect, useState } from 'react'
import { t } from '@/utils/i18n'
import { useLanguage } from '@/hooks/useLanguage'
import { useDream } from '@/hooks/useDream'
import { usePlan } from '@/hooks/usePlan'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useLoading } from '@/store/contexts/LoadingContext'
import { useProUpgrade } from '@/hooks/useProUpgrade'
import { DreamityGoalResDto } from '@/store/types/goal.types'
import { DreamityPlanResDto, PlanFrequency } from '@/store/types/plan.types'
import { dreamityPlannerRepository } from '@/services/dreamityPlannerRepository'
import Modal from '@/components/common/Modal/Modal'
import Button from '@/components/common/Button/Button'
import { Input } from '@/components/common/Input/Input'
import './PlanCreateModal.css'

interface Props {
  isOpen: boolean
  onClose: () => void
  initialDreamId?: string | null
  initialGoalId?: string | null
  /** 캘린더에서 열 때 선택한 날짜 (yyyy-MM-dd) */
  initialStartDate?: string | null
  fromCalendar?: boolean
  /** 생성 성공 시 호출. goalId와 갱신된 계획 목록(중복 fetch 방지용) 전달 */
  onCreated?: (goalId?: string, plans?: DreamityPlanResDto[]) => void
}

export default function PlanCreateModal({
  isOpen,
  onClose,
  initialDreamId = null,
  initialGoalId = null,
  initialStartDate = null,
  fromCalendar = false,
  onCreated,
}: Props) {
  const { language } = useLanguage()
  const { dreams } = useDream()
  const { createPlan } = usePlan()
  const { showToast } = useToastContext()
  const { startLoading, stopLoading } = useLoading()
  const { handleRestrictionError } = useProUpgrade()

  const todayStr = new Date().toISOString().split('T')[0]

  const [form, setForm] = useState({
    planName: '',
    planDesc: '',
    frequency: 'NONE' as PlanFrequency,
    frequencyDay: null as number | null,
    startDate: todayStr,
    endDate: todayStr,
  })
  const [selectedDreamId, setSelectedDreamId] = useState<string | null>(initialDreamId ?? null)
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(initialGoalId ?? null)
  const [availableGoals, setAvailableGoals] = useState<DreamityGoalResDto[]>([])
  const [loading, setLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setSelectedDreamId(initialDreamId ?? null)
    setSelectedGoalId(initialGoalId ?? null)
  }, [initialDreamId, initialGoalId])

  useEffect(() => {
    if (!selectedDreamId) {
      setAvailableGoals([])
      return
    }
    let mounted = true
    const load = (dreamId: string) => {
      setLoading(true)
      try {
        const goals = dreamityPlannerRepository.listGoals(dreamId)
        if (mounted) setAvailableGoals(goals)
      } catch (err) {
        console.error(err)
        if (mounted) setAvailableGoals([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load(selectedDreamId as string)
    return () => {
      mounted = false
    }
  }, [selectedDreamId])

  useEffect(() => {
    if (!isOpen) {
      setForm({
        planName: '',
        planDesc: '',
        frequency: 'NONE',
        frequencyDay: null,
        startDate: todayStr,
        endDate: todayStr,
      })
      // keep selectedDream/Goal as provided by initial props
    } else if (fromCalendar && initialStartDate) {
      // 캘린더에서 열 때 선택한 날짜를 시작일로 설정
      const endDefault = new Date(initialStartDate)
      endDefault.setDate(endDefault.getDate() + 30)
      setForm((prev) => ({
        ...prev,
        startDate: initialStartDate,
        endDate: endDefault.toISOString().split('T')[0],
      }))
    }
  }, [isOpen, todayStr, fromCalendar, initialStartDate])

  // when selectedDreamId or frequency changes, if frequency is not NONE use dream's start/end dates
  useEffect(() => {
    if (!selectedDreamId) return
    const selectedDream = dreams.find(d => d.dreamId === selectedDreamId)
    if (!selectedDream) return
    if (form.frequency !== 'NONE') {
      setForm((prev) => ({
        ...prev,
        startDate: selectedDream.startDate || prev.startDate,
        endDate: selectedDream.endDate || prev.endDate,
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDreamId, form.frequency])

  const handleSubmit = async () => {
    if (!form.planName.trim()) return
    if (!selectedGoalId) {
      alert(t('messages.goalRequired', language) || (language === 'ko' ? '목표를 선택해주세요.' : 'Please select a goal.'))
      return
    }
    if (new Date(form.startDate) > new Date(form.endDate)) {
      alert(t('messages.invalidDateRange', language) || (language === 'ko' ? '시작일은 종료일보다 이전이어야 합니다.' : 'Invalid date range'))
      return
    }
    try {
      setIsSubmitting(true)
      startLoading()
      const dreamToUse = selectedDreamId ?? initialDreamId
      if (!dreamToUse) {
        alert(t('messages.dreamRequired', language) || (language === 'ko' ? '꿈을 선택해주세요.' : 'Please select a dream.'))
        setIsSubmitting(false)
        return
      }
      const plans = await createPlan(dreamToUse, {
        dreamId: dreamToUse,
        goalId: selectedGoalId,
        planName: form.planName.trim(),
        planDesc: form.planDesc.trim(),
        frequency: form.frequency,
        frequencyDay: form.frequency === 'NONE' ? null : form.frequencyDay,
        isCompleted: false,
        startDate: form.startDate,
        endDate: form.endDate,
      })
      showToast(t('messages.createSuccess', language) || (language === 'ko' ? '생성되었습니다.' : 'Created successfully.'), 'success')
      onClose()
      onCreated && onCreated(selectedGoalId ?? undefined, plans)
    } catch (err: any) {
      console.error(err)
      const msg = err?.response?.data?.message || err?.message || t('messages.createFailed', language) || 'Plan 생성 실패'
      setIsSubmitting(false)
      const handled = await handleRestrictionError(err)
      if (!handled) {
        showToast(msg, 'error')
      }
      return
    } finally {
      // ensure spinner is off
      setIsSubmitting(false)
      stopLoading()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={() => !isSubmitting && onClose()} title={t('goalDetail.createPlanTitle', language) || '계획 생성'} size="md">
      <div className="plan-create-modal bg-white">
        {fromCalendar && (
          <div className="plan-create-modal__group">
            <label>{t('calendar.dreamSelect', language) || '꿈 선택'}</label>
            <select className="plan-create-modal__select" value={selectedDreamId || ''} onChange={(e) => {
              const v = e.target.value || null
              setSelectedDreamId(v)
              setSelectedGoalId(null)
            }}>
              <option value="">{t('calendar.selectDream', language) || '꿈을 선택하세요'}</option>
              {dreams.map(d => <option key={d.dreamId} value={d.dreamId}>{d.dreamName}</option>)}
            </select>
          </div>
        )}

        {fromCalendar && (
          <div className="plan-create-modal__group">
            <label>{t('calendar.goalSelect', language) || '목표 선택'}</label>
            <select className="plan-create-modal__select" value={selectedGoalId || ''} onChange={(e) => setSelectedGoalId(e.target.value || null)} disabled={!selectedDreamId || loading}>
              <option value="">{loading ? (t('common.loading', language) || '로딩 중...') : (t('calendar.selectGoal', language) || '목표를 선택하세요')}</option>
              {availableGoals.map(g => <option key={g.goalId} value={g.goalId}>{g.goalName}</option>)}
            </select>
          </div>
        )}

        {/* If opened from GoalList (not fromCalendar) and dream/goal preselected, show read-only fields */}
        <Input label={t('goalDetail.planName', language) || '계획 이름'} value={form.planName} onChange={(e) => setForm({ ...form, planName: e.target.value })} required />
        <Input label={t('goalDetail.planDesc', language) || '계획 설명'} value={form.planDesc} onChange={(e) => setForm({ ...form, planDesc: e.target.value })} placeholder={language === 'ko' ? '계획 설명 (선택)' : 'Plan description (optional)'} />
        <div className="plan-create-modal__form-row">
          <div className="plan-create-modal__group">
            <label>{t('goalDetail.frequency', language) || '빈도'}</label>
            <select
              className="plan-create-modal__select"
              value={form.frequency}
              onChange={(e) => {
                const newFreq = e.target.value as PlanFrequency
                const selectedDream = dreams.find(d => d.dreamId === selectedDreamId)
                if (newFreq === 'NONE') {
                  setForm({
                    ...form,
                    frequency: newFreq,
                    frequencyDay: null,
                    endDate: form.startDate,
                  })
                } else {
                  setForm({
                    ...form,
                    frequency: newFreq,
                    frequencyDay: null,
                    startDate: selectedDream?.startDate || form.startDate,
                    endDate: selectedDream?.endDate || form.endDate,
                  })
                }
              }}
            >
              <option value="NONE">{t('plan.frequency.none', language) || '없음'}</option>
              <option value="DAILY">{t('plan.frequency.daily', language) || '매일'}</option>
              <option value="WEEKLY">{t('plan.frequency.weekly', language) || '매주'}</option>
              <option value="MONTHLY">{t('plan.frequency.monthly', language) || '매월'}</option>
              <option value="YEARLY">{t('plan.frequency.yearly', language) || '매년'}</option>
            </select>
          </div>
          {form.frequency === 'WEEKLY' && (
            <div className="plan-create-modal__group">
              <label>{t('goalDetail.frequencyDay', language) || '요일 선택'}</label>
              <select
                className="plan-create-modal__select"
                value={form.frequencyDay || ''}
                onChange={(e) => setForm({ ...form, frequencyDay: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">{t('calendar.selectDay', language) || '요일을 선택하세요'}</option>
                <option value="1">{t('calendar.monday', language) || '월요일'}</option>
                <option value="2">{t('calendar.tuesday', language) || '화요일'}</option>
                <option value="3">{t('calendar.wednesday', language) || '수요일'}</option>
                <option value="4">{t('calendar.thursday', language) || '목요일'}</option>
                <option value="5">{t('calendar.friday', language) || '금요일'}</option>
                <option value="6">{t('calendar.saturday', language) || '토요일'}</option>
                <option value="7">{t('calendar.sunday', language) || '일요일'}</option>
              </select>
            </div>
          )}
          {form.frequency === 'MONTHLY' && (
            <div className="plan-create-modal__group">
              <label>{t('goalDetail.frequencyDay', language) || (language === 'ko' ? '일 선택' : 'Select day')}</label>
              <select
                className="plan-create-modal__select"
                value={form.frequencyDay || ''}
                onChange={(e) => setForm({ ...form, frequencyDay: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">{t('calendar.selectDay', language) || (language === 'ko' ? '일을 선택하세요' : 'Select day')}</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}{language === 'ko' ? '일' : ''}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="plan-create-modal__row">
          <div className="plan-create-modal__group">
            <label>{t('goalDetail.startDate', language) || '시작일'}</label>
            <Input
              type="date"
              value={form.startDate}
              onChange={(e) => {
                const newStart = e.target.value
                if (form.frequency === 'NONE') {
                  setForm({ ...form, startDate: newStart, endDate: newStart })
                } else {
                  setForm({ ...form, startDate: newStart })
                }
              }}
            />
          </div>
          <div className="plan-create-modal__group">
            <label>{t('goalDetail.endDate', language) || '종료일'}</label>
            <Input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              readOnly={form.frequency === 'NONE'}
              disabled={form.frequency === 'NONE'}
            />
          </div>
        </div>

        <div className="plan-create-modal__actions">
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>{t('common.create', language)}</Button>
        </div>
      </div>
    </Modal>
  )
}


